import { X, Lock, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Constants } from "@/constants";

interface UnlockFeaturePopupProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  features?: Array<{
    title: string;
    description: string;
  }>;
  ctaTitle?: string;
  ctaDescription?: string;
  buttonText?: string;
  benefits?: string[];
}

export function UnlockFeaturePopup({
  isOpen,
  onClose,
  title = "Unlock Pro Features!",
  description = "Upgrade now to access all features and grow your business",
  features = [
    {
      title: "Full customer list & details",
      description:
        "View complete customer information including contact details, transaction history, average spend, and visit tracking"
    },
    {
      title: "Complete transaction history",
      description:
        "Monitor customer transactions, spending patterns, visit frequency and marketing permissions in one place"
    },
    {
      title: "Advanced filters & reports",
      description:
        "Organize customers into segments like All Customers, Reachable, First Time, Inactive, and Imported for targeted management"
    },
    {
      title: "SMS Campaigns & more",
      description:
        "Import customer data from spreadsheets and other systems with a simple import tool to quickly build your database"
    }
  ],
  ctaTitle = "Ready to Transform Your Business?",
  ctaDescription = "Join thousands of businesses already using MarketBuzz CRM to boost customer relationships and drive growth",
  buttonText = "Install MarketBuzz CRM Now"
}: UnlockFeaturePopupProps) {
  if (!isOpen) return null;

  const handleSubscribe = () => {
    window.open(Constants.CLOVER_MARKETPLACE_URL, "_blank");
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl relative max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 z-10"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Content */}
        <div className="p-6">
          <div className="text-center space-y-6">
            {/* Header with Icons */}
            <div className="relative">
              <div className="flex items-center justify-center gap-2 text-xl font-semibold mb-2">
                <Lock className="h-6 w-6 text-blue-600" />
                <h2>{title}</h2>
              </div>

              <p className="text-gray-600 max-w-md mx-auto">{description}</p>
            </div>

            {/* Feature Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
              {features.map((feature, index) => (
                <div key={index} className="text-left p-4 border rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="h-2 w-2 bg-blue-600 rounded-full mt-2 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">
                        {feature.title}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Section */}
            <div className="bg-blue-50 p-4 -mx-6 ">
              <div className="flex items-center justify-center gap-2 text-lg font-semibold mb-2">
                <Zap className="h-5 w-5 text-yellow-500" />
                <h3>{ctaTitle}</h3>
              </div>

              <p className="text-gray-600 mb-4 max-w-md mx-auto">
                {ctaDescription}
              </p>

              <Button
                onClick={handleSubscribe}
                className="w-full max-w-md bg-blue-600 hover:bg-blue-700 text-white py-3 text-lg font-semibold"
              >
                <Zap className="h-5 w-5 mr-2" />
                {buttonText}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
